import { ExtractKeysExtend } from '../../components/helpers/helpers';
import {
    CompanyThresholdsMode,
    MonitorAircraftOptions,
    MonitorAircraftSettingsBeaconCode,
    MonitorAirportOptions
} from '../../types/thresholds';
import { defaultAirportMonitorOptions, defaultAircraftMonitorOptions as defaults } from '../monitor-defaults';

type Tab = 'company' | 'user';

export type EffectiveMonitorAircraftOptions = {
    monitorOriginDepartureMinMinutes: number;
    monitorOriginWithoutDepartureTime: boolean;
    monitorDestinationDepartureMinMinutes: number;
    monitorDestinationWithoutArrivalTime: boolean;
    monitorAltDepartureMinMinutes: number;
    monitorAltWithoutArrivalTime: boolean;
    emergencyBeacons: MonitorAircraftSettingsBeaconCode[];
    ignoredCompanyBeaconCodes: number[];
    altitudeChangeWindowMinutes: number;
    suppressAltitudeMonitorMiles: number;
    monitorImpactDistance: {
        volcanicAsh: number;
        sigmet: number;
        convSigment: number;
        dsua: number;
        edrTurbulence: number;
        turbulence: number;
        icing: number;
        contrail: number;
        tcf: number;
    };
};

export type EffectiveMonitorAirportOptions = {
    tafWindowHours: number;
    airportMonitorStatusInclude: {
        originThresholds: boolean;
        destinationThresholds: boolean;
        alternateThresholds: boolean;
    };
};

export function mergeAircraftOptions(
    tab: Tab,
    data: {
        user: MonitorAircraftOptions;
        company: MonitorAircraftOptions;
    },
    mode: CompanyThresholdsMode
): EffectiveMonitorAircraftOptions {
    const dataSet = data[tab];

    if (tab === 'company' || mode === CompanyThresholdsMode.notUsed || mode === CompanyThresholdsMode.specified) {
        return mergeAircraftOptionsWithDefaults(dataSet);
    }

    // User
    if (mode === CompanyThresholdsMode.template) {
        return mergeAircraftOptionsTemplate(data.user, data.company);
    }

    if (mode === CompanyThresholdsMode.minimum) {
        return mergeAircraftOptionsMinimum(data.user, data.company);
    }

    throw new Error('Unknown company mode');
}

export function mergeAirportOptions(
    tab: Tab,
    data: { user: MonitorAirportOptions; company: MonitorAirportOptions },
    mode: CompanyThresholdsMode
): EffectiveMonitorAirportOptions {
    if (tab === 'company' || mode === CompanyThresholdsMode.notUsed || mode === CompanyThresholdsMode.specified) {
        // All company and user company not used modes
        return {
            tafWindowHours: data[tab].tafWindowHours ?? defaultAirportMonitorOptions.tafWindowHours,
            // prettier-ignore
            airportMonitorStatusInclude: {
                originThresholds: data[tab].airportMonitorStatusInclude.originThresholds ?? defaultAirportMonitorOptions.airportMonitorStatusInclude.originThresholds,
                destinationThresholds: data[tab].airportMonitorStatusInclude.destinationThresholds ?? defaultAirportMonitorOptions.airportMonitorStatusInclude.destinationThresholds,
                alternateThresholds: data[tab].airportMonitorStatusInclude.alternateThresholds ?? defaultAirportMonitorOptions.airportMonitorStatusInclude.alternateThresholds,
            }
        };
    }

    if (mode === CompanyThresholdsMode.template) {
        return {
            tafWindowHours: data.user.tafWindowHours ?? data.company.tafWindowHours ?? defaultAirportMonitorOptions.tafWindowHours,
            // prettier-ignore
            airportMonitorStatusInclude: {
                originThresholds: data.user.airportMonitorStatusInclude.originThresholds ?? data.company.airportMonitorStatusInclude.originThresholds ?? defaultAirportMonitorOptions.airportMonitorStatusInclude.originThresholds,
                destinationThresholds: data.user.airportMonitorStatusInclude.destinationThresholds ?? data.company.airportMonitorStatusInclude.destinationThresholds ?? defaultAirportMonitorOptions.airportMonitorStatusInclude.destinationThresholds,
                alternateThresholds: data.user.airportMonitorStatusInclude.alternateThresholds ?? data.company.airportMonitorStatusInclude.alternateThresholds ?? defaultAirportMonitorOptions.airportMonitorStatusInclude.alternateThresholds
            }
        };
    }

    if (mode === CompanyThresholdsMode.minimum) {
        return {
            tafWindowHours: mergeMinimumNumbers(Math.max, 'tafWindowHours', data.user, data.company, defaultAirportMonitorOptions),
            airportMonitorStatusInclude: {
                originThresholds: mergeMinimumBooleans(
                    'originThresholds',
                    data.user.airportMonitorStatusInclude,
                    data.company.airportMonitorStatusInclude,
                    defaultAirportMonitorOptions.airportMonitorStatusInclude
                ),
                destinationThresholds: mergeMinimumBooleans(
                    'destinationThresholds',
                    data.user.airportMonitorStatusInclude,
                    data.company.airportMonitorStatusInclude,
                    defaultAirportMonitorOptions.airportMonitorStatusInclude
                ),
                alternateThresholds: mergeMinimumBooleans(
                    'alternateThresholds',
                    data.user.airportMonitorStatusInclude,
                    data.company.airportMonitorStatusInclude,
                    defaultAirportMonitorOptions.airportMonitorStatusInclude
                )
            }
        };
    }

    throw new Error('Unknown company mode');
}

function mergeAircraftOptionsWithDefaults(dataSet: MonitorAircraftOptions): EffectiveMonitorAircraftOptions {
    return {
        monitorOriginDepartureMinMinutes: dataSet.monitorOriginDepartureMinMinutes ?? defaults.monitorOriginDepartureMinMinutes,
        monitorOriginWithoutDepartureTime: dataSet.monitorOriginWithoutDepartureTime ?? defaults.monitorOriginWithoutDepartureTime,
        monitorDestinationDepartureMinMinutes:
            dataSet.monitorDestinationDepartureMinMinutes ?? defaults.monitorDestinationDepartureMinMinutes,
        monitorDestinationWithoutArrivalTime: dataSet.monitorDestinationWithoutArrivalTime ?? defaults.monitorDestinationWithoutArrivalTime,
        monitorAltDepartureMinMinutes: dataSet.monitorAltDepartureMinMinutes ?? defaults.monitorAltDepartureMinMinutes,
        monitorAltWithoutArrivalTime: dataSet.monitorAltWithoutArrivalTime ?? defaults.monitorAltWithoutArrivalTime,
        emergencyBeacons: dataSet.emergencyBeacons,
        ignoredCompanyBeaconCodes: dataSet.ignoredCompanyBeaconCodes,
        altitudeChangeWindowMinutes: dataSet.altitudeChangeWindowMinutes ?? defaults.altitudeChangeWindowMinutes,
        suppressAltitudeMonitorMiles: dataSet.suppressAltitudeMonitorMiles ?? defaults.suppressAltitudeMonitorMiles,
        monitorImpactDistance: {
            volcanicAsh: dataSet.monitorImpactDistance.volcanicAsh ?? defaults.monitorImpactDistance.volcanicAsh,
            sigmet: dataSet.monitorImpactDistance.sigmet ?? defaults.monitorImpactDistance.sigmet,
            convSigment: dataSet.monitorImpactDistance.convSigment ?? defaults.monitorImpactDistance.convSigment,
            dsua: dataSet.monitorImpactDistance.dsua ?? defaults.monitorImpactDistance.dsua,
            edrTurbulence: dataSet.monitorImpactDistance.edrTurbulence ?? defaults.monitorImpactDistance.edrTurbulence,
            turbulence: dataSet.monitorImpactDistance.turbulence ?? defaults.monitorImpactDistance.turbulence,
            icing: dataSet.monitorImpactDistance.icing ?? defaults.monitorImpactDistance.icing,
            contrail: dataSet.monitorImpactDistance.contrail ?? defaults.monitorImpactDistance.contrail,
            tcf: dataSet.monitorImpactDistance.tcf ?? defaults.monitorImpactDistance.tcf
        }
    };
}

function mergeAircraftOptionsTemplate(
    dataSet: MonitorAircraftOptions,
    companySet: MonitorAircraftOptions
): EffectiveMonitorAircraftOptions {
    //prettier-ignore
    return {
        monitorOriginDepartureMinMinutes: mergeTemplate('monitorOriginDepartureMinMinutes', dataSet, companySet, defaults ),
        monitorOriginWithoutDepartureTime: mergeTemplate('monitorOriginWithoutDepartureTime', dataSet, companySet, defaults),
        monitorDestinationDepartureMinMinutes: mergeTemplate('monitorDestinationDepartureMinMinutes', dataSet, companySet, defaults),
        monitorDestinationWithoutArrivalTime: mergeTemplate('monitorDestinationWithoutArrivalTime', dataSet, companySet, defaults),
        monitorAltDepartureMinMinutes: mergeTemplate('monitorAltDepartureMinMinutes', dataSet, companySet, defaults),
        monitorAltWithoutArrivalTime: mergeTemplate('monitorAltWithoutArrivalTime', dataSet, companySet, defaults),
        emergencyBeacons: mergeBeaconCodesTemplate(
            dataSet.emergencyBeacons,
            companySet.emergencyBeacons,
            dataSet.ignoredCompanyBeaconCodes
        ),
        ignoredCompanyBeaconCodes: dataSet.ignoredCompanyBeaconCodes,
        altitudeChangeWindowMinutes: mergeTemplate('altitudeChangeWindowMinutes', dataSet, companySet, defaults),
        suppressAltitudeMonitorMiles: mergeTemplate('suppressAltitudeMonitorMiles', dataSet, companySet, defaults),
        monitorImpactDistance: {
            volcanicAsh: mergeTemplate('volcanicAsh', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            sigmet: mergeTemplate('sigmet', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            convSigment: mergeTemplate('convSigment', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            dsua: mergeTemplate('dsua', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            edrTurbulence: mergeTemplate('edrTurbulence', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            turbulence: mergeTemplate('turbulence', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            icing: mergeTemplate('icing', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            contrail: mergeTemplate('contrail', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            tcf: mergeTemplate('tcf', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
        }
    };
}

function mergeAircraftOptionsMinimum(dataSet: MonitorAircraftOptions, companySet: MonitorAircraftOptions): EffectiveMonitorAircraftOptions {
    return {
        monitorOriginDepartureMinMinutes: mergeMinimumNumbers(Math.max, 'monitorOriginDepartureMinMinutes', dataSet, companySet, defaults),
        monitorOriginWithoutDepartureTime:
            (dataSet.monitorOriginWithoutDepartureTime ?? defaults.monitorOriginWithoutDepartureTime) ||
            (companySet.monitorOriginWithoutDepartureTime ?? defaults.monitorOriginWithoutDepartureTime),
        monitorDestinationDepartureMinMinutes: mergeMinimumNumbers(
            Math.max,
            'monitorDestinationDepartureMinMinutes',
            dataSet,
            companySet,
            defaults
        ),
        monitorDestinationWithoutArrivalTime:
            (dataSet.monitorDestinationWithoutArrivalTime ?? defaults.monitorDestinationWithoutArrivalTime) ||
            (companySet.monitorDestinationWithoutArrivalTime ?? defaults.monitorDestinationWithoutArrivalTime),
        monitorAltDepartureMinMinutes: mergeMinimumNumbers(Math.max, 'monitorAltDepartureMinMinutes', dataSet, companySet, defaults),
        monitorAltWithoutArrivalTime:
            (dataSet.monitorAltWithoutArrivalTime ?? defaults.monitorAltWithoutArrivalTime) ||
            (companySet.monitorAltWithoutArrivalTime ?? defaults.monitorAltWithoutArrivalTime),
        emergencyBeacons: mergeBeaconCodesMinimum(dataSet.emergencyBeacons, companySet.emergencyBeacons),
        ignoredCompanyBeaconCodes: dataSet.ignoredCompanyBeaconCodes,
        altitudeChangeWindowMinutes: mergeMinimumNumbers(Math.max, 'altitudeChangeWindowMinutes', dataSet, companySet, defaults),
        suppressAltitudeMonitorMiles: mergeMinimumNumbers(Math.min, 'suppressAltitudeMonitorMiles', dataSet, companySet, defaults),
        // prettier-ignore
        monitorImpactDistance: {
            volcanicAsh: mergeMinimumNumbers(Math.max, 'volcanicAsh', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            sigmet: mergeMinimumNumbers(Math.max, 'sigmet', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            convSigment: mergeMinimumNumbers(Math.max, 'convSigment', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            dsua: mergeMinimumNumbers(Math.max, 'dsua', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            edrTurbulence: mergeMinimumNumbers(Math.max, 'edrTurbulence', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            turbulence: mergeMinimumNumbers(Math.max, 'turbulence', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            icing: mergeMinimumNumbers(Math.max, 'icing', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            contrail: mergeMinimumNumbers(Math.max, 'contrail', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance),
            tcf: mergeMinimumNumbers(Math.max, 'tcf', dataSet.monitorImpactDistance, companySet.monitorImpactDistance, defaults.monitorImpactDistance)
        }
    };
}

function mergeBeaconCodesTemplate(
    setA: MonitorAircraftSettingsBeaconCode[],
    setB: MonitorAircraftSettingsBeaconCode[],
    ignored: number[]
): MonitorAircraftSettingsBeaconCode[] {
    const newSet = [...setA];
    for (const item of setB) {
        if (newSet.find((x) => x.code === item.code) || ignored.includes(item.code)) {
            continue;
        }
        newSet.push(item);
    }

    return newSet;
}

function mergeBeaconCodesMinimum(
    setA: MonitorAircraftSettingsBeaconCode[],
    setB: MonitorAircraftSettingsBeaconCode[]
): MonitorAircraftSettingsBeaconCode[] {
    const newSet = [...setA];
    for (const item of setB) {
        if (newSet.find((x) => x.code === item.code)) {
            continue;
        }
        newSet.push(item);
    }

    return newSet;
}

function mergeTemplate<T, K extends keyof T>(key: K, setA: T, setB: T, defaults: { [_ in K]: NonNullable<T[K]> }): NonNullable<T[K]> {
    return setA[key] ?? setB[key] ?? defaults[key];
}

function mergeMinimumBooleans<T, K extends ExtractKeysExtend<T, boolean | null>>(
    key: K,
    setA: T,
    setB: T,
    defaults: { [_ in K]: boolean }
): boolean {
    const defaultValue = defaults[key];
    const a = setA[key];
    const b = setB[key];
    if (a === null && b === null) {
        return defaultValue;
    }

    if (a === null) {
        return b as boolean;
    }

    if (b === null) {
        return a as boolean;
    }

    return !!a || !!b || defaultValue;
}

function mergeMinimumNumbers<T, K extends ExtractKeysExtend<T, number | null>>(
    func: typeof Math.min,
    key: K,
    setA: T,
    setB: T,
    defaults: { [_ in K]: number }
): number {
    const defaultValue = defaults[key];
    const a = setA[key];
    const b = setB[key];
    if (a === null && b === null) {
        return defaultValue;
    }

    if (a === null) {
        return b as number;
    }

    if (b === null) {
        return a as number;
    }

    return func(a as number, b as number);
}
